import { jsPDF } from "jspdf";

export default function usePdf() {
  const generatePdf = (brief, answers, clientName) => {
    const doc = new jsPDF();
    const bg = [7, 17, 29]; // #07111d
    const gold = [240, 169, 88]; // #f0a958
    const white = [247, 239, 228]; // #f7efe4
    const muted = [125, 139, 160]; // #7d8ba0

    const margin = 28;
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();

    const drawHeader = (title) => {
      doc.setFillColor(...bg);
      doc.rect(0, 0, pageWidth, 45, "F");
      
      // Top accent line
      doc.setDrawColor(...gold);
      doc.setLineWidth(1);
      doc.line(margin, 15, pageWidth - margin, 15);

      doc.setTextColor(...gold);
      doc.setFont("courier", "bold");
      doc.setFontSize(8);
      doc.text("SUTRANODE STUDIO / DISCOVERY", margin, 25);
      
      doc.setTextColor(...white);
      doc.setFont("helvetica", "bold");
      doc.setFontSize(22);
      doc.text(title.toUpperCase(), margin, 38);
    };

    const drawFooter = (pageNum, total) => {
      doc.setFont("courier", "normal");
      doc.setFontSize(7);
      doc.setTextColor(...muted);
      doc.text(`PROJECT BRIEF EXPORT — ${clientName.toUpperCase()}`, margin, pageHeight - 15);
      doc.text(`${pageNum} OF ${total}`, pageWidth - margin - 15, pageHeight - 15);
      
      // Bottom accent line
      doc.setDrawColor(...gold);
      doc.setLineWidth(0.2);
      doc.line(margin, pageHeight - 20, pageWidth - margin, pageHeight - 20);
    };

    // --- PAGE 1: DRAMATIC COVER ---
    doc.setFillColor(...bg);
    doc.rect(0, 0, pageWidth, pageHeight, "F");
    
    // Architectural Border Design
    doc.setDrawColor(...gold);
    doc.setLineWidth(1.5);
    doc.line(10, 10, 40, 10); // Top left corner
    doc.line(10, 10, 10, 40);
    doc.line(pageWidth - 40, pageHeight - 10, pageWidth - 10, pageHeight - 10); // Bottom right corner
    doc.line(pageWidth - 10, pageHeight - 40, pageWidth - 10, pageHeight - 10);

    // Centered Content
    doc.setTextColor(...gold);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(14);
    doc.text("PROJECT BRIEF", pageWidth / 2, 80, { align: "center" });

    doc.setTextColor(...white);
    doc.setFontSize(64);
    doc.text("THE", pageWidth / 2, 110, { align: "center" });
    doc.setFontSize(72);
    doc.setTextColor(...gold);
    doc.text("VISION", pageWidth / 2, 135, { align: "center" });
    doc.setTextColor(...white);
    doc.setFontSize(54);
    doc.text("DOCUMENT", pageWidth / 2, 160, { align: "center" });

    doc.setDrawColor(...gold);
    doc.setLineWidth(0.5);
    doc.line(pageWidth / 2 - 20, 175, pageWidth / 2 + 20, 175);

    doc.setFontSize(18);
    doc.setTextColor(...white);
    doc.text(clientName.toUpperCase(), pageWidth / 2, 195, { align: "center" });

    doc.setFontSize(9);
    doc.setTextColor(...muted);
    doc.setFont("courier", "normal");
    doc.text("SUTRANODE.IN", pageWidth / 2, pageHeight - 30, { align: "center" });

    // --- CONTENT PAGES ---
    brief.sections.forEach((section, sIdx) => {
      doc.addPage();
      doc.setFillColor(...bg);
      doc.rect(0, 0, pageWidth, pageHeight, "F");
      
      drawHeader(section.title);

      let y = 65;
      doc.setDrawColor(...gold);
      
      section.questions.forEach((q) => {
        const answer = answers[q.id];
        if (answer === undefined || answer === "") return;

        // Auto-page break logic
        const labelLines = doc.splitTextToSize(q.label, pageWidth - (margin * 2) - 15);
        let answerText = String(answer);
        if (Array.isArray(answer)) answerText = answer.join(", ");
        const answerLines = doc.splitTextToSize(answerText, pageWidth - (margin * 2) - 25);
        
        const totalHeight = (labelLines.length * 6) + (answerLines.length * 5) + 25;

        if (y + totalHeight > pageHeight - 30) {
          doc.addPage();
          doc.setFillColor(...bg);
          doc.rect(0, 0, pageWidth, pageHeight, "F");
          drawHeader(section.title);
          y = 65;
        }

        // Question tag (e.g. Q01)
        doc.setFont("courier", "bold");
        doc.setFontSize(7);
        doc.setTextColor(...gold);
        doc.text(q.id.toUpperCase(), margin, y);

        // Question Label (Bolder, bigger)
        doc.setFont("helvetica", "bold");
        doc.setFontSize(13);
        doc.setTextColor(...white);
        doc.text(labelLines, margin + 8, y + 1);
        y += (labelLines.length * 6) + 4;

        // Answer Layout (Architectural indent)
        doc.setFont("helvetica", "normal");
        doc.setFontSize(11);
        doc.setTextColor(...white);
        doc.text(answerLines, margin + 12, y + 8);
        
        // Left accent bar
        doc.setDrawColor(...gold);
        doc.setLineWidth(0.5);
        doc.line(margin + 5, y, margin + 5, y + (answerLines.length * 5) + 10);
        
        y += (answerLines.length * 5) + 20;
      });
    });

    // --- FINAL PAGE: ARCHITECTURAL THANK YOU ---
    doc.addPage();
    doc.setFillColor(...bg);
    doc.rect(0, 0, pageWidth, pageHeight, "F");
    
    doc.setDrawColor(...gold);
    doc.setLineWidth(0.2);
    doc.line(margin, pageHeight / 2, pageWidth - margin, pageHeight / 2);

    doc.setTextColor(...gold);
    doc.setFontSize(48);
    doc.text("SHAPING", margin, pageHeight / 2 - 20);
    doc.setTextColor(...white);
    doc.text("THE FUTURE.", margin, pageHeight / 2 + 30);

    doc.setFontSize(11);
    doc.setTextColor(...muted);
    const closing = "We've received your brief. Our design team is currently mapping these responses to our architectural framework for your project.";
    const closingLines = doc.splitTextToSize(closing, 100);
    doc.text(closingLines, margin, pageHeight / 2 + 55);

    doc.setTextColor(...gold);
    doc.setFontSize(10);
    doc.text("SUTRANODE STUDIO", pageWidth - margin - 40, pageHeight - margin);

    // Apply Footer to all content pages
    const totalPages = doc.internal.getNumberOfPages();
    for (let i = 1; i <= totalPages; i++) {
      doc.setPage(i);
      drawFooter(i, totalPages);
    }

    doc.save(`SUTRANODE_BRIEF_${clientName.replace(/\s+/g, '_').toUpperCase()}.pdf`);
  };

  return { generatePdf };
}
